import { useNavigate } from 'react-router-dom';
import {
  Activity,
  FlaskConical,
  MoreHorizontal,
  Pencil,
  Power,
  Trash2,
  Upload,
} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui';
import { routes } from '../../lib/routes';
import { isPlatformEntity } from '../../types';
import type { Agent } from '../../types';

interface Props {
  agent: Agent;
  spaceId: string;
  /** Caller can edit agents in this space (`useEntityPermissions().canEdit`). */
  canEdit: boolean;
  /** Caller may delete THIS agent (`useEntityPermissions().canDelete(agent)`). */
  canDelete: boolean;
  onToggleStatus: () => void;
  onPublish: () => void;
  onDelete: () => void;
  /** Disables the trigger while a mutation the menu started is in flight. */
  isBusy?: boolean;
  align?: 'end' | 'start';
}

/**
 * The `⋯` overflow menu for an agent, shared by the agents list rows and the
 * agent detail header so both surfaces offer exactly the same actions.
 *
 * Visibility rules:
 *   - `Open in Sandbox` and `View agent runs` are always shown — both are
 *     read-only and valid even for platform agents.
 *   - Every mutating action (`Edit`, `Publish`, `Activate`/`Deactivate`) needs
 *     `canEdit` and is hidden for platform entities, which aren't editable.
 *   - `Activate`/`Deactivate` is further hidden for archived agents (there is
 *     no un-archive path here).
 *   - `Delete` is gated on `canDelete`, which the caller computes per-agent —
 *     admins, or the agent's own creator.
 *
 * Note for table rows: the agents table has an `onRowClick`, so wrap this in a
 * `stopPropagation` container or opening the menu also navigates.
 */
export default function AgentActionsMenu({
  agent,
  spaceId,
  canEdit,
  canDelete,
  onToggleStatus,
  onPublish,
  onDelete,
  isBusy,
  align = 'end',
}: Props) {
  const navigate = useNavigate();
  const isPlatform = isPlatformEntity(agent);

  const showMutations = canEdit && !isPlatform;
  const showToggle = showMutations && agent.status !== 'archived';
  const showDelete = canDelete && !isPlatform;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          disabled={isBusy}
          aria-label="Agent actions"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        <DropdownMenuItem
          onSelect={() => navigate(routes.console.spaceSandboxFor(spaceId, agent.agentId))}
        >
          <FlaskConical className="h-4 w-4" />
          Open in Sandbox
        </DropdownMenuItem>

        {showMutations && (
          <>
            <DropdownMenuItem
              onSelect={() => navigate(routes.console.spaceAgentEdit(spaceId, agent.agentId))}
            >
              <Pencil className="h-4 w-4" />
              Edit Agent
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onPublish}>
              <Upload className="h-4 w-4" />
              Publish Version
            </DropdownMenuItem>
          </>
        )}

        {showToggle && (
          <DropdownMenuItem onSelect={onToggleStatus}>
            <Power className="h-4 w-4" />
            {agent.status === 'active' ? 'Deactivate' : 'Activate'}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate(routes.console.runsForAgent(agent.agentId))}>
          <Activity className="h-4 w-4" />
          View agent runs
        </DropdownMenuItem>

        {showDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={onDelete}
              className="text-red-600 focus:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
              Delete Agent
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
